<template>
  <div>
    <icon
      v-if="loading"
      class="spinner spinner--search"
      icon-name="loader-fa"
      view-box="0 0 28 28"
    />
    <!-- search results stakeholders -->
    <stakeholder-result-list
      v-if="searchStakeholdersVisible"
      :empty-message="noSearchResultsText"
      :stakeholders="searchStakeholders"
      :is-search-result="true"
      :show-button="false"
      :has-error="stakeholderError"
      :error-message="errorMessage"
      @click="clickBackToList"
    />
    <!-- address results stakeholders -->
    <stakeholder-result-list
      v-if="addressStakeholdersVisible"
      :empty-message="noAddressResultsText"
      :stakeholders="addressStakeholders"
      :show-button="addressStakeholders.length !== 0 && !showRelationshipForm"
      :button-disabled="relationships.length === 0"
      :button-text="continueText"
      :has-error="stakeholderError"
      :error-message="errorMessage"
      @click="clickContinue"
    />
    <p
      v-if="infiniteScrollLoading"
      class="blink">Fetching more stakeholders...</p>
  </div>
</template>

<script>
/* eslint-disable consistent-return */
import { isEmpty as _isEmpty } from "lodash";
import Icon from "StyleguideComponents/Icon.vue";
import Routes from "Constants/campaign/whodoyouknow/routeNames";
import { mapState } from "vuex";
import StakeholderResultList from "WhoDoYouKnowComponents/StakeholderResultList.vue";
import Stakeholder from "WhoDoYouKnowComponents/Stakeholder.vue";
import AccordionTransition from "Assets/js/transitions/whodoyouknow/AccordionTransition.vue";
import InfiniteScrollHelper from "JS/helpers/whodoyouknow/infiniteScrollHelper";

export default {
  name: "StakeholderResults",
  components: {
    Icon,
    AccordionTransition,
    Stakeholder,
    StakeholderResultList
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (_isEmpty(vm.prefetchStakeholdersPayload)) {
        vm.$router.push({ name: Routes.LOOKUP_STAKEHOLDERS });
      }
    });
  },
  data() {
    return {
      continueText: "Continue",
      emptySearchResultsMessage: "",
      errorMessage: "",
      infiniteScrollHelper: "",
      infiniteScrollLoading: false,
      localSearchStakeholders: "",
      noAddressResultsText:
        "No officials were found for your address. Try searching for officials above",
      showRelationshipForm: false
    };
  },
  computed: {
    ...mapState("wdyk", {
      addressStakeholders: state => state.addressStakeholders,
      loading: state => state.loading,
      relationships: state => state.relationships,
      searchStakeholders: state => state.searchStakeholders,
      searchTerm: state => state.searchTerm,
      stakeholderError: state => state.stakeholderError,
      prefetchStakeholdersPayload: state => state.prefetchStakeholdersPayload
    }),
    addressStakeholdersVisible() {
      return !this.loading && this.searchBarEmpty;
    },
    noSearchResultsText() {
      return `Sorry, we couldn't find any results for '${
        this.searchTerm
      }'. Please try again.`;
    },
    searchStakeholdersVisible() {
      return !this.loading && !this.searchBarEmpty;
    },
    searchBarEmpty() {
      return _isEmpty(this.searchTerm);
    }
  },
  inject: {
    stakeholderService: { from: "stakeholderService" },
    eventsBus: { from: "eventsBus" }
  },
  mounted() {
    // Infinite scrolling instance
    this.infiniteScrollHelper = new InfiniteScrollHelper(
      this.$store,
      this.$router,
      this.eventsBus
    );

    // Contains all infinite scroll events
    this.initializeScrollingEvents(this.infiniteScrollHelper);

    this.eventsBus.$on("stakeholderError", message => {
      this.errorMessage = message;
    });

    this.eventsBus.$on("showRelationshipForm", visibility => {
      this.showRelationshipForm = visibility;
    });
  },
  methods: {
    clickBackToList() {
      this.stakeholderService.clearSearch();
    },
    clickContinue() {
      this.eventsBus.$emit("showStakeholderSearchPopup");
    },
    fetchMoreStakeholders() {
      const moreSearchStakeholdersAvailable = this.infiniteScrollHelper.moreSearchStakeholdersAvailable(
        this.searchStakeholdersVisible
      );

      if (moreSearchStakeholdersAvailable && !this.infiniteScrollLoading) {
        return this.infiniteScrollHelper.fetchMoreSearchStakeholders(
          this.searchTerm
        );
      }
    },
    initializeScrollingEvents(scrollHelper) {
      // Detect infinite scrolling
      this.eventsBus.$on("actionSectionScroll", event => {
        const atBottomOfPage = scrollHelper.detectBottomOfElement(event);
        if (atBottomOfPage && !this.showRelationshipForm) {
          this.fetchMoreStakeholders();
        }
      });

      // If we're making an infinite scroll request, show the loading indicator
      this.eventsBus.$on(
        "infiniteScrollLoading",
        this.toggleInfiniteScrollLoading
      );

      // StakeholderSearch emits this event each time we type in the stakeholder search bar
      this.eventsBus.$on(
        "updateSearchStakeholdersLastBatch",
        searchStakeholders => {
          scrollHelper.updateSearchStakeholdersLastBatch(searchStakeholders);
        }
      );
    },
    toggleInfiniteScrollLoading(bool) {
      this.infiniteScrollLoading = bool;
    }
  }
};
</script>
<style>
.blink {
  animation: 1s blink infinite ease-in-out;
  margin: 1rem auto;
  text-align: center;
}
@keyframes blink {
  50% {
    opacity: 0.6;
  }
}
</style>
